import type { ReactNode } from "react";
import RouteNotFoundPage from "@/pages/system/route-not-found";
import { NotAvailableState } from "@/components/ui/system-state";
import { isFeatureEnabled, type FeatureFlag } from "@/lib/feature-flags";

/**
 * Route- or section-level feature-flag gate (ALL-5039, LANES.md HW-L9a:
 * "fixture-free page shells behind a feature flag").
 *
 * Flags are build-time only (lib/feature-flags.ts): a flag is on when the
 * build set `VITE_FEATURE_<FLAG>`, and nothing a browser can do turns it on.
 *
 * A closed gate never renders a placeholder message — no "coming soon", no
 * "not implemented", nothing that invites a return visit to a page with no
 * date on it. It renders one of two honest states:
 *
 *  - by default, exactly what an unregistered route renders — the real 404
 *    page — so a disabled route is indistinguishable from a route that does
 *    not exist yet, and never invites a bookmark to a page that might
 *    disappear before the flag flips;
 *  - when the caller names the reason (`reason`), an explicit "not enabled in
 *    this build" panel that says why the section is closed and who can enable
 *    it. Use this for a section whose existence the user already knows about
 *    (a linked console, a tab they were told about), where the honest answer
 *    is the reason and the enabling path, not a 404.
 *  - when the caller supplies a `fallback` element, that element instead of
 *    either of the above. Use this to send the visitor somewhere real — for a
 *    route that is reachable by link or by typing the address while its flag
 *    is off, a 404 is the wrong answer and a redirect is the right one.
 */
export function FeatureGate({
  flag,
  reason,
  enabledBy,
  fallback,
  children,
}: {
  flag: FeatureFlag;
  /**
   * Why this section is closed. Supplying it renders the reason panel instead
   * of the 404 page. Takes precedence over nothing: a `fallback` wins over it.
   */
  reason?: string;
  /**
   * Who can enable it. Defaults to the release owner who builds this
   * workspace, because the flag is fixed at build time and no in-app setting
   * can turn it on.
   */
  enabledBy?: string;
  /**
   * Rendered in place of the closed state when the flag is off — normally a
   * `<Navigate to="…" replace />`. A section that a stray link or a typed
   * address can reach is better routed to a page that works than shown a 404,
   * so this takes precedence over `reason` and over the default 404 when the
   * caller names both.
   */
  fallback?: ReactNode;
  children: ReactNode;
}) {
  if (isFeatureEnabled(flag)) return <>{children}</>;

  if (fallback !== undefined) return <>{fallback}</>;

  if (reason === undefined) return <RouteNotFoundPage />;

  return (
    <main className="flex min-h-screen w-full items-center justify-center p-6">
      <div className="w-full max-w-[640px]">
        <NotAvailableState
          title="Not enabled in this build"
          description={reason}
          reason={
            enabledBy ??
            "The Alloy release owner who builds this workspace can enable it: the setting is fixed when the frontend is built, so no in-app setting turns it on."
          }
        />
      </div>
    </main>
  );
}
